/**
 * Versioniertes Krypto-Envelope. Steht bewusst VOR der ersten Zeile Chat-Code fest,
 * damit die E2EE-Einführung in Phase 5 ein Cipher-Wechsel ist und kein Schema-Bruch.
 *
 * Layout (Big Endian):
 *   0        version    uint8
 *   1..2     suite      uint16
 *   3..4     headerLen  uint16
 *   5..5+h   header     (bei v0 leer; bei v1 MLS-Framing: group_id, epoch, sender, seq)
 *   rest     payload    (bei v0 UTF-8-Klartext, bei v1 MLS-Ciphertext)
 *
 * Der Server liest ausschließlich version/suite/epoch/seq — für Reihenfolge und
 * Replay-Schutz. Er interpretiert den Payload nie.
 */
export const ENVELOPE_VERSION = {
    /** Phase 4: kein E2EE. Die UI zeigt in diesem Modus KEIN Schloss-Symbol. */
    PLAIN: 0x00,
    /** Ab Phase 5: MLS (RFC 9420). */
    MLS: 0x01,
};
export const CIPHER_SUITE = {
    NONE: 0x0000,
    /** MLS_128_DHKEMX25519_AES128GCM_SHA256_Ed25519 */
    MLS_1: 0x0001,
    /** MLS_256_DHKEMX25519_CHACHA20POLY1305_SHA256_Ed25519 */
    MLS_3: 0x0003,
};
export const MAX_ENVELOPE_BYTES = 1024 * 1024; // 1 MiB; größere Inhalte gehören in einen Blob
export class EnvelopeError extends Error {
}
export function encodeEnvelope(env) {
    const header = encodeHeader(env.version, env.header);
    if (header.length > 0xffff)
        throw new EnvelopeError('header too large');
    const out = new Uint8Array(5 + header.length + env.payload.length);
    const view = new DataView(out.buffer);
    out[0] = env.version;
    view.setUint16(1, env.suite);
    view.setUint16(3, header.length);
    out.set(header, 5);
    out.set(env.payload, 5 + header.length);
    if (out.length > MAX_ENVELOPE_BYTES)
        throw new EnvelopeError('envelope too large');
    return out;
}
export function decodeEnvelope(bytes) {
    if (bytes.length < 5)
        throw new EnvelopeError('envelope too short');
    if (bytes.length > MAX_ENVELOPE_BYTES)
        throw new EnvelopeError('envelope too large');
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const version = bytes[0];
    if (version !== ENVELOPE_VERSION.PLAIN && version !== ENVELOPE_VERSION.MLS) {
        throw new EnvelopeError(`unknown envelope version ${bytes[0] ?? -1}`);
    }
    const suite = view.getUint16(1);
    const headerLen = view.getUint16(3);
    if (5 + headerLen > bytes.length)
        throw new EnvelopeError('header length out of range');
    return {
        version,
        suite,
        header: decodeHeader(version, bytes.subarray(5, 5 + headerLen)),
        payload: bytes.subarray(5 + headerLen),
    };
}
/**
 * Was der Server aus einem Envelope lesen darf — und mehr nicht.
 * Bewusst eine eigene, schmale Funktion, damit im Server-Code nirgends
 * versehentlich auf `payload` zugegriffen wird.
 */
export function readRoutingInfo(bytes) {
    const { version, suite, header } = decodeEnvelope(bytes);
    return { version, suite, epoch: header.epoch ?? null, seq: header.seq ?? null };
}
// --- interne Kodierung -------------------------------------------------------
function encodeHeader(version, h) {
    if (version === ENVELOPE_VERSION.PLAIN)
        return new Uint8Array(0);
    const groupId = h.groupId ?? new Uint8Array(0);
    if (groupId.length > 0xff)
        throw new EnvelopeError('groupId too long');
    const out = new Uint8Array(1 + groupId.length + 8 + 4 + 8);
    const view = new DataView(out.buffer);
    let off = 0;
    out[off++] = groupId.length;
    out.set(groupId, off);
    off += groupId.length;
    view.setBigUint64(off, h.epoch ?? 0n);
    off += 8;
    view.setUint32(off, h.senderIndex ?? 0);
    off += 4;
    view.setBigUint64(off, h.seq ?? 0n);
    return out;
}
function decodeHeader(version, bytes) {
    if (version === ENVELOPE_VERSION.PLAIN)
        return {};
    const idLen = bytes[0];
    if (idLen === undefined)
        throw new EnvelopeError('malformed header');
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    if (1 + idLen + 20 > bytes.length)
        throw new EnvelopeError('malformed header');
    let off = 1;
    const groupId = bytes.subarray(off, off + idLen);
    off += idLen;
    const epoch = view.getBigUint64(off);
    off += 8;
    const senderIndex = view.getUint32(off);
    off += 4;
    const seq = view.getBigUint64(off);
    return { groupId, epoch, senderIndex, seq };
}
// --- Bequemlichkeit für Phase 4 ---------------------------------------------
export function plainEnvelope(text) {
    return encodeEnvelope({
        version: ENVELOPE_VERSION.PLAIN,
        suite: CIPHER_SUITE.NONE,
        header: {},
        payload: new TextEncoder().encode(text),
    });
}
export function readPlainEnvelope(bytes) {
    const env = decodeEnvelope(bytes);
    if (env.version !== ENVELOPE_VERSION.PLAIN) {
        throw new EnvelopeError('not a plain envelope — this content is end-to-end encrypted');
    }
    return new TextDecoder().decode(env.payload);
}
//# sourceMappingURL=envelope.js.map